import { GlobalWorkerOptions, getDocument } from 'pdfjs-dist';
import type { TextItem } from 'pdfjs-dist/types/src/display/api';
import workerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url';

GlobalWorkerOptions.workerSrc = workerUrl;

export interface PdfExtractionProgress {
  page: number;
  totalPages: number;
  progress: number;
}

export type PdfProgressCallback = (progress: PdfExtractionProgress) => void;

function isTextItem(item: unknown): item is TextItem {
  return (
    typeof item === 'object' &&
    item !== null &&
    'str' in item &&
    typeof (item as { str?: unknown }).str === 'string'
  );
}

function pageText(items: readonly unknown[]): string {
  let text = '';
  for (const item of items) {
    if (!isTextItem(item)) continue;
    text += item.str;
    text += item.hasEOL ? '\n' : ' ';
  }
  return text
    .replace(/[ \t]+/g, ' ')
    .replace(/ *\n */g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

/**
 * Extract text from a PDF Blob entirely in the browser. PDF.js receives only
 * the in-memory ArrayBuffer; no file contents are uploaded or fetched.
 */
export async function extractPdfText(
  file: Blob,
  onProgress?: PdfProgressCallback,
): Promise<string> {
  if (typeof window === 'undefined' || typeof file.arrayBuffer !== 'function') {
    throw new Error('PDF extraction is only available in a browser.');
  }

  const bytes = await file.arrayBuffer();
  const loadingTask = getDocument({ data: bytes });
  const document = await loadingTask.promise;
  const pages: string[] = [];

  try {
    for (let pageNumber = 1; pageNumber <= document.numPages; pageNumber += 1) {
      const page = await document.getPage(pageNumber);
      const content = await page.getTextContent();
      pages.push(pageText(content.items));
      onProgress?.({
        page: pageNumber,
        totalPages: document.numPages,
        progress: pageNumber / document.numPages,
      });
      page.cleanup();
    }
  } finally {
    await loadingTask.destroy();
  }

  const text = pages.filter(Boolean).join('\n\n').trim();
  if (!text) {
    throw new Error(
      'No selectable text was found in this PDF. It may be scanned or image-only; run OCR and try again.',
    );
  }
  return text;
}

export const extractTextFromPdf = extractPdfText;
